import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { UserSelector } from '@/components/shared/UserSelector';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { useLinkStore } from '@/stores/linkStore';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'supplier');

const supplierSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().optional(),
  criticality: z.enum(['Very Low', 'Low', 'Medium', 'High', 'Very High']),
  notes: z.string().optional(),
  last_assessment: z.string().optional(),
  next_review: z.string().optional(),
  questionnaire_score: z.number().min(0).max(100).optional(),
  issues_count: z.number().min(0).optional(),
  owner_id: z.string().optional(),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface SupplierEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: any;
  onSave: (id: string, data: any) => void;
  isSaving?: boolean;
}

export function SupplierEditDialog({ open, onOpenChange, supplier, onSave, isSaving }: SupplierEditDialogProps) {
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  const { loadLinks, links, bulkCreateLinks } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      criticality: 'Medium',
      issues_count: 0,
    },
  });

  useEffect(() => {
    if (supplier && open) {
      // Normalize legacy criticality values to 5-level scale
      let normalizedCriticality = supplier.criticality || 'Medium';
      const critMap: Record<string, string> = {
        'low': 'Low',
        'medium': 'Medium',
        'high': 'High',
        'critical': 'Very High',
      };
      if (critMap[normalizedCriticality.toLowerCase()]) {
        normalizedCriticality = critMap[normalizedCriticality.toLowerCase()];
      }
      
      reset({
        name: supplier.name || '',
        category: supplier.category || '',
        criticality: normalizedCriticality as any,
        notes: supplier.notes || '',
        last_assessment: supplier.last_assessment || '',
        next_review: supplier.next_review || '',
        questionnaire_score: supplier.questionnaire_score || undefined,
        issues_count: supplier.issues_count || 0,
        owner_id: supplier.owner_id || '',
      });

      // Load existing links
      loadLinks('supplier', supplier.id).then(() => {
        const supplierLinks = links.filter(l => 
          (l.source_type === 'supplier' && l.source_id === supplier.id) ||
          (l.target_type === 'supplier' && l.target_id === supplier.id)
        );
        
        const existingLinks: SelectedEntity[] = supplierLinks.map(l => {
          const isSource = l.source_type === 'supplier';
          const type = (isSource ? l.target_type : l.source_type) as EntityType;
          const id = isSource ? l.target_id : l.source_id;
          return {
            id,
            type,
            name: resolveEntityName(type, id),
          };
        });
        setSelectedLinks(existingLinks);
      });
    }
  }, [supplier, open, reset, loadLinks, links, resolveEntityName]);

  const lastAssessment = watch('last_assessment');
  const nextReview = watch('next_review');

  const onSubmit = async (data: SupplierFormData) => {
    // Handle link updates
    if (selectedLinks.length > 0) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);
      
      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('supplier', supplier.id, targetType as EntityType, targetIds);
      }
    }
    
    onSave(supplier.id, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Supplier</DialogTitle>
          <DialogDescription>
            Update supplier details and assessment information
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Supplier Name *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Enter supplier name"
            />
            {errors.name && (
              <p className="text-sm text-danger mt-1">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                {...register('category')}
                placeholder="e.g., IT Services, Cloud Provider"
              />
            </div>

            <div>
              <Label htmlFor="criticality">Criticality *</Label>
              <Select
                value={watch('criticality')}
                onValueChange={(value: any) => setValue('criticality', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Very Low">Very Low</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Very High">Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Last Assessment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !lastAssessment && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {lastAssessment ? format(new Date(lastAssessment), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={lastAssessment ? new Date(lastAssessment) : undefined}
                    onSelect={(date) => setValue('last_assessment', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Next Review Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !nextReview && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {nextReview ? format(new Date(nextReview), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextReview ? new Date(nextReview) : undefined}
                    onSelect={(date) => setValue('next_review', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="questionnaire_score">Questionnaire Score (0-100)</Label>
              <Input
                id="questionnaire_score"
                type="number"
                min="0"
                max="100"
                {...register('questionnaire_score', { valueAsNumber: true })}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label htmlFor="issues_count">Issues Count</Label>
              <Input
                id="issues_count"
                type="number"
                min="0"
                {...register('issues_count', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Additional notes about this supplier"
              className="min-h-[100px]"
            />
          </div>

          <UserSelector
            value={watch('owner_id')}
            onChange={(userId) => setValue('owner_id', userId)}
            label="Relationship Owner"
            placeholder="Select owner"
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
